import { ColumnDef } from '@tanstack/react-table';
import Big from 'big.js';
import dayjs from 'dayjs';
import { cloneDeep, isArray, isEmpty, toNumber, trimStart, uniq } from 'lodash';
import { ReactNode } from 'react';

import { getReportCfvEmissionsDataApproval } from '@/apis/report/cfv/emissions/data-approval';
import { getReportCfvEmissionsDataCollection } from '@/apis/report/cfv/emissions/data-collection';
import { getReportCfvEmissionsDataEntry } from '@/apis/report/cfv/emissions/data-entry';
import { ConfigVersion } from '@/apis/user/space/configs.dto';
import CreateEmissionConstants from '@/app/org-cfv/src/constants/CreateEmissionConstants';
import {
  ActivityUnit,
  CatOfActivityRelatedToFuelEnergy,
  HypotheticalSituationType,
} from '@/app/org-cfv/src/enums/ActivityDataTypeEnum';
import {
  DataActivityStatus,
  EmissionFactorByFactorErrorType,
  EmissionSourceCategory,
  EmissionSourceFactorSource,
  EmissionSourceGreenHouseGases,
  EmissionSourceSubCategory,
} from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { FugitiveEquipmentType, RefrigerantGHG } from '@/app/org-cfv/src/enums/FugitiveEmissionEnum';
import {
  Approval,
  ApprovalLog,
  GetReviewActivityDataResponse,
  ReviewEmissionsType,
} from '@/app/org-cfv/src/typings/ActivityDataDetailsTypings';
import { EmissionsType } from '@/app/org-cfv/src/typings/CreateActivityDataTypings';
import { TabItem } from '@/components/common/DataDisplay/tabs/ScrollableTabs/ScrollableTabs';
import { CO2E, OTHER } from '@/constants/common';
import { APP_DATE_FORMAT } from '@/constants/date';
import { NextTFunction, safeTranslate } from '@/i18n/client';
import { Document } from '@/typings/common';
import { FugitiveCategoryType, getFugitiveCategoryType, getWastewaterType, WastewaterType } from '@/utils/emission';
import { formatFullDecimal, formatNum } from '@/utils/numbers';
import { isNumber } from '@/utils/validations';

import { EmissionType } from '../../src/typings/EmissionSourceTypings';
import {
  CreateEmissionType,
  EQUIPMENT_NUMBER_BEGIN,
  GasData,
  PROCESS_NUMBER_BEGIN,
  WasteWaterSchemaTypeFieldPath,
} from '../emission-sources/create/formValidation';
import { getAllSubcategoriesHeader } from './tableSettings';
import { ActivityDataType, OrgCFVRoute } from './typings';

const EMPTY_WASTEWATER_STATE = {
  treatmentMethod: null,
  dischargeLocation: null,
  dischargePathwayOrSystemType: null,
  wastewaterTreatmentRate: null,
  maxMethaneProduction: null,
  methaneCorrectionFactor: null,
  organicMatterProductionPPPD: null,
  ch4IndustrialWastewaterDischargeFactor: null,
  ch4EmissionFactor: null,
  proteinSupplyPPPD: null,
  nitrogenToProteinRatio: null,
  nonProteinSaturatedWastewaterFactor: null,
  n2oIndustrialWastewaterDischargeFactor: null,
  n2oEmissionFactor: null,
} as const;

const EMPTY_SEWAGE_POOL_STATE = {
  bodFactor: null,
  averageWastewaterConcentration: null,
  perCapitaHourlyWastewaterVolume: null,
  treatmentEfficiency: null,
} as const;

export const DISABLED_CATEGORIES = [EmissionSourceCategory.CAT_6];

export const getSearchParamsByEmission = (emission: EmissionType | null, withPagination?: boolean) => {
  if (!emission) {
    return new URLSearchParams();
  }
  const params = new URLSearchParams(withPagination ? location.search : undefined);
  const { category, subCategory } = emission;

  params.set('category', category);
  params.set('subCategory', subCategory);
  if (emission.id) {
    params.set('emissionId', emission.id);
  } else if (emission.emissionGroupId) {
    params.set('emissionGroupId', emission.emissionGroupId);
  }
  return params;
};

export const table = null;

export const getRows = (t: NextTFunction, data?: GetReviewActivityDataResponse) => {
  if (!data || data.data.length <= 0) {
    return [];
  }

  return data.data.map((emissions) => ({
    ...emissions,
    documentDate: `${dayjs(emissions.documentDate).format(APP_DATE_FORMAT)}${
      emissions.category === EmissionSourceCategory.CAT_2 && emissions.documentEndDate
        ? ` - ${dayjs(emissions.documentEndDate).format(APP_DATE_FORMAT)}`
        : ''
    }`,
    // TODO: this need to be costumed cell view
    attachments:
      emissions.documents && emissions.documents.length > 0
        ? emissions.documents.map((attachment) => attachment.fileId).join(', ')
        : '',
    status: t(`common.status-${emissions.status}`),
    metadata: {
      ...emissions.metadata,
      unit: emissions.metadata.unit ? t(`unit.${emissions.metadata.unit}`) : '',
    },
  }));
};

export const getHeaderTitles = (
  t: NextTFunction,
  route: OrgCFVRoute,
  subCategory: EmissionSourceSubCategory,
  onViewAttachmentClick: (file: Document) => void,
): ColumnDef<Approval & EmissionsType>[] =>
  getAllSubcategoriesHeader(t, onViewAttachmentClick)?.[route]?.[subCategory] ?? [];

export const getActivityProgressString = (t: NextTFunction, data: ReviewEmissionsType): string => {
  switch (data.approval.status) {
    case DataActivityStatus.CREATED:
    case DataActivityStatus.SUBMITTED:
      return t('org-cfv-data-entry.dataSubmitted');
    case DataActivityStatus.APPROVED:
      return t('org-cfv-data-entry.dataApproved');
    case DataActivityStatus.REJECTED:
      const isBulkInput = data.approval.isBatch;
      const normalMsg = `${t('org-cfv-data-approval.dataRejected')}`;
      const details = `${t('org-cfv-data-entry.date')}: ${dayjs(data.reviewedAt).format(APP_DATE_FORMAT)}
      ${t('org-cfv-data-entry.reason')}: ${data.reason}`;
      return isBulkInput
        ? normalMsg
        : `${normalMsg}
      ${details}`;
    case DataActivityStatus.PROCESSING:
      return t('bulk-input.dataIsBeingCreatedPleaseWait');
    default:
      return '';
  }
};

export const getOrgCFVRouteByPathname = (pathname: string): OrgCFVRoute | null => {
  const splitedPathname = pathname.split('/');

  if (splitedPathname.length <= 0) {
    return null;
  }

  return splitedPathname[splitedPathname.length - 1] as OrgCFVRoute;
};

export const getEmissionListApi = (pageType: ActivityDataType) => {
  switch (pageType) {
    default:
    case ActivityDataType.ENTRY:
      return getReportCfvEmissionsDataEntry;
    case ActivityDataType.APPROVAL:
      return getReportCfvEmissionsDataApproval;
    case ActivityDataType.COLLECTION:
      return getReportCfvEmissionsDataCollection;
  }
};

export const transferEmissionType = (t: NextTFunction, row: EmissionType) => {
  if (
    row?.subCategory === EmissionSourceSubCategory.CAT_4_3 ||
    row?.subCategory === EmissionSourceSubCategory.CAT_4_5
  ) {
    return row?.metadata?.emissionType ? t(`org-cfv-create-emission.${row.metadata.emissionType as string}`) : '';
  } else {
    return '';
  }
};

export const transferRawMaterial = (t: NextTFunction, row?: EmissionType) => {
  if (isEmpty(row)) {
    return '';
  }

  const subCategory = row?.subCategory;
  const metadata = row?.metadata;
  const commaConcat = (arr: string[]) => arr.filter((v) => v).join(', ');

  switch (subCategory) {
    case EmissionSourceSubCategory.CAT_1_1:
    case EmissionSourceSubCategory.CAT_1_2:
      return commaConcat([
        metadata?.rawMaterial1 ? t(`raw-material.${metadata.rawMaterial1}`) : '',
        metadata?.rawMaterial2 ? t(`raw-material.${metadata.rawMaterial2}`) : '',
      ]);
    case EmissionSourceSubCategory.CAT_1_3:
      return commaConcat([
        metadata?.reportRawMaterial1
          ? t(`org-cfv-create-emission.rawMaterialProduct-${metadata?.reportRawMaterial1}`)
          : '',
        metadata?.rawMaterial2 === OTHER.PERMP_OTHER
          ? metadata.rawMaterial2OtherName || t('common.other')
          : metadata?.rawMaterial2
          ? t(`process-emission-industry.${metadata.rawMaterial2}`)
          : '',
      ]);
    case EmissionSourceSubCategory.CAT_4_1B:
      const value = metadata?.rawMaterial4_1b;
      return value
        ? value.startsWith('RM_PROD')
          ? t(`org-cfv-create-emission.rawMaterialOrProduct41b-${value}`)
          : value
          ? t(`raw-material.${value}`)
          : ''
        : '';
    case EmissionSourceSubCategory.CAT_4_3:
      return commaConcat([
        metadata?.emissionType ? t(`org-cfv-create-emission.${metadata.emissionType as string}`) : '',
        metadata?.reportRawMaterial2 ? t(`raw-material-product.${metadata.reportRawMaterial2}`) : '',
      ]);
    default:
      return commaConcat([
        metadata?.reportRawMaterial1
          ? t(`org-cfv-create-emission.rawMaterialProduct-${metadata.reportRawMaterial1}`)
          : '',
        metadata?.reportRawMaterial2 ? t(`raw-material-product.${metadata.reportRawMaterial2}`) : '',
      ]);
  }
};

export const transferMarketAndLocation = (t: NextTFunction, row?: EmissionType) => {
  const marketBase = !!row?.metadata.marketBased;
  const locationBase = !!row?.metadata.locationBased;

  if (marketBase && locationBase) {
    return `${t('common.marketBased')}, ${t('common.localBased')}`;
  }
  return locationBase ? t('common.localBased') : t('common.marketBased');
};

const convertFactorToNumber = (gasData: GasData) => {
  return (gasData || []).reduce((data, { gasKey, factor, ...current }) => {
    if (!gasKey?.length) {
      return data;
    }
    return data.concat({
      ...current,
      gasKey,
      factor: Number(factor),
    });
  }, [] as GasData);
};

export const transferEmissionBody = (emission: CreateEmissionType): CreateEmissionType => {
  const result = cloneDeep(emission);
  const { fugitiveCategory } = result.metadata;
  const fugitiveCategoryType = getFugitiveCategoryType(fugitiveCategory);
  if (!result.id) {
    delete result.id;
  }

  if (
    emission.subCategory === EmissionSourceSubCategory.CAT_4_1B &&
    result.metadata.relatedFuelEnergyCategory !== CatOfActivityRelatedToFuelEnergy.CARFE_4
  ) {
    result.factors = result.factors?.filter((factor) => factor.factorSourceType === EmissionSourceFactorSource.DEFAULT);
  }
  result.factors = result.factors?.map((factor) => {
    if (!factor.activationDate) {
      return factor;
    }
    if (factor.gasData) {
      factor.gasData = convertFactorToNumber(factor.gasData);
    }
    return {
      ...factor,
      activationDate: dayjs(factor.activationDate).format('YYYY-MM-DD') as unknown as Date,
    };
  });

  // transfer ghgs
  if (Array.isArray(result.factors) && result.factors.length > 0) {
    let factorErrorType: EmissionFactorByFactorErrorType | null = null;

    for (const factor of result.factors) {
      if (factorErrorType === null && factor.factorErrorType) {
        factorErrorType = factor.factorErrorType;
      } else if (factorErrorType) {
        factor.factorErrorType = factorErrorType;
      }
    }
  }

  const clearCat1_4OthersTypeValue = () => {
    const { equipmentType } = result.metadata;
    const isRefrigerant = fugitiveCategoryType === FugitiveCategoryType.REFRIGERANT;
    const shouldKeepFugitiveCalculation =
      isRefrigerant && result.metadata.fugitiveCalculationMethod === HypotheticalSituationType.AVG_FUGITIVE_METH;

    // clear fugitive calculation
    if (!shouldKeepFugitiveCalculation && result.metadata.fugitiveRateCalculation) {
      result.metadata.fugitiveCalculationMethod = undefined;
      result.metadata.fugitiveRateCalculation = undefined;
    }

    // clear refrigerant
    const isAerosolSprayHFCs =
      fugitiveCategoryType === FugitiveCategoryType.AEROSOL_SPRAY &&
      result.metadata.greenHouseEmissionType[0] === EmissionSourceGreenHouseGases.HFCS;
    const shouldKeepRefrigerant = isRefrigerant || isAerosolSprayHFCs;

    if (!shouldKeepRefrigerant) {
      result.metadata.refrigerantComposition = [];
    }

    const shouldKeepWastewater = fugitiveCategoryType === FugitiveCategoryType.WASTEWATER;
    // clear waste water
    if (!shouldKeepWastewater && result.metadata.customWastewater.treatmentMethod) {
      result.metadata.customWastewater = EMPTY_WASTEWATER_STATE;
    }

    const shouldKeepSepticTank = equipmentType === FugitiveEquipmentType.ET_55;
    // clear septic tank
    if (!shouldKeepSepticTank && result.metadata.sewagePool.bodFactor) {
      result.metadata.sewagePool = EMPTY_SEWAGE_POOL_STATE;
    }
  };

  // transform refrigerant compositions
  if (
    !(
      result.subCategory === EmissionSourceSubCategory.CAT_1_4 &&
      (isAerosolSprayWithHfcs(result.metadata.greenHouseEmissionType, result.metadata.fugitiveCategory) ||
        fugitiveCategoryType === FugitiveCategoryType.REFRIGERANT)
    )
  ) {
    result.metadata.refrigerantComposition = [];
    result.metadata.fugitiveCalculationMethod = undefined;
    result.metadata.fugitiveRateCalculation = undefined;
  }

  if (result.subCategory === EmissionSourceSubCategory.CAT_1_4) {
    // 所有有 calculation 的
    if (fugitiveCategoryType === FugitiveCategoryType.REFRIGERANT) {
      if (result.metadata.fugitiveCalculationMethod === HypotheticalSituationType.AVG_FUGITIVE_METH) {
        if (result.metadata.fugitiveRateCalculation) {
          // transfer custom refrigerant factor params
          result.metadata.fugitiveRateCalculation.emissionFactor = toNumber(
            result.metadata.fugitiveRateCalculation.emissionFactor,
          );
          result.metadata.fugitiveRateCalculation.recoveryEfficiency = toNumber(
            result.metadata.fugitiveRateCalculation.recoveryEfficiency,
          );
          result.metadata.fugitiveRateCalculation.preventionEquipmentUsageRate = toNumber(
            result.metadata.fugitiveRateCalculation.preventionEquipmentUsageRate,
          );
        }
      } else {
        result.metadata.fugitiveRateCalculation = undefined;
      }
    } else if (fugitiveCategoryType === FugitiveCategoryType.WASTEWATER) {
      const keys = Object.keys(result.metadata.customWastewater) as WasteWaterSchemaTypeFieldPath[];
      keys?.forEach((key) => {
        if (result.metadata.customWastewater[key] !== null && isNumber(result.metadata.customWastewater[key])) {
          (result.metadata.customWastewater[key] as null | number) = toNumber(result.metadata.customWastewater[key]);
        }
      });
    } else if (result.metadata.equipmentType === FugitiveEquipmentType.ET_55) {
      // transfer custom sewage pool factor params
      result.metadata.sewagePool.bodFactor = toNumber(result.metadata.sewagePool.bodFactor);
      result.metadata.sewagePool.averageWastewaterConcentration = toNumber(
        result.metadata.sewagePool.averageWastewaterConcentration,
      );
      result.metadata.sewagePool.perCapitaHourlyWastewaterVolume = toNumber(
        result.metadata.sewagePool.perCapitaHourlyWastewaterVolume,
      );
      result.metadata.sewagePool.treatmentEfficiency = toNumber(result.metadata.sewagePool.treatmentEfficiency);
    }

    clearCat1_4OthersTypeValue();
  }

  // transfer stationary sources license code
  if ([EmissionSourceCategory.CAT_1, EmissionSourceCategory.CAT_2].includes(emission.category)) {
    const { isStationarySourcesLicense, processNumber, equipmentNumber } = result.metadata;

    result.metadata.processNumber = isStationarySourcesLicense
      ? `${PROCESS_NUMBER_BEGIN}${trimStart(processNumber, PROCESS_NUMBER_BEGIN)}`
      : '';
    result.metadata.equipmentNumber = isStationarySourcesLicense
      ? `${EQUIPMENT_NUMBER_BEGIN}${trimStart(equipmentNumber, EQUIPMENT_NUMBER_BEGIN)}`
      : '';
  }

  return result;
};

export const getCategoryTabs = (
  t: NextTFunction,
  {
    categories,
    renderAppendInfo,
    renderTabElement,
  }: {
    categories: Partial<Record<EmissionSourceCategory, Partial<Record<EmissionSourceSubCategory, boolean>>>>;
    renderAppendInfo?: (category: EmissionSourceCategory) => string | number;
    renderTabElement: (category: EmissionSourceCategory) => ReactNode;
  },
) =>
  Object.keys(categories)
    .map((categoryStr) => {
      const category = categoryStr as EmissionSourceCategory;
      const isActive = Object.values(categories[category] ?? {}).some((active) => active);

      return {
        id: category,
        tab: `${t('common.category')} ${category}`,
        testId: `cat-${parseInt(category) - 1}`,
        disabled: !isActive,
        disabledText: !isActive ? t('common.comingSoon') : undefined,
        element: renderTabElement(category),
        appendInfo: isActive && typeof renderAppendInfo === 'function' ? renderAppendInfo(category) : undefined,
      };
    })
    .filter((tab) => !tab.disabled);

export const addCategory6 = (
  t: NextTFunction,
  tabs: TabItem<EmissionSourceCategory>[],
  orgVersion: ConfigVersion,
): TabItem<EmissionSourceCategory>[] => {
  if (orgVersion === 'PRO') {
    tabs.push({
      id: EmissionSourceCategory.CAT_6,
      tab: `${t('common.category')} ${EmissionSourceCategory.CAT_6}`,
      element: null,
      disabled: DISABLED_CATEGORIES.includes(EmissionSourceCategory.CAT_6),
      testId: `cat-${EmissionSourceCategory.CAT_6}`,
    });
  }
  return tabs;
};

interface GhgWithFamilyInfo {
  ghg?: string;
  isRefrigerant?: boolean;
  isPFCS?: boolean;
}

export const getGhgFamily = ({ ghg, isRefrigerant, isPFCS }: GhgWithFamilyInfo): EmissionSourceGreenHouseGases => {
  let ghgKey = (ghg || '') as EmissionSourceGreenHouseGases;
  if (isRefrigerant) {
    ghgKey = EmissionSourceGreenHouseGases.HFCS;
  } else if (isPFCS) {
    ghgKey = EmissionSourceGreenHouseGases.PFCS;
  } else if (ghg === CO2E) {
    // TODO: should use isCo2 flag from BE
    ghgKey = EmissionSourceGreenHouseGases.CO2;
  }

  // TODO: [SPRINT 35] workaround for GHG key not in enum, remove when BE returns isRefrigerant and isPFCS
  if (!Object.values(EmissionSourceGreenHouseGases).includes(ghgKey)) {
    if (Object.values(RefrigerantGHG).includes(ghgKey as unknown as RefrigerantGHG)) {
      ghgKey = EmissionSourceGreenHouseGases.HFCS;
    } else {
      ghgKey = EmissionSourceGreenHouseGases.PFCS;
    }
  }
  return ghgKey;
};

export const getGhgFamilyI18nListByFactors = (
  t: NextTFunction,
  factors: CreateEmissionType['factors'],
  metadataGreenHouseEmissionType: string[] = [], // TODO: [SPRINT 35] workaround for empty factor.greenHouseEmissionType, remove when greenHouseEmissionType is not empty
) => {
  const ghgI18nList =
    factors
      ?.map(
        (factor) =>
          factor.gasData
            ?.filter((g) => {
              const ghgs =
                factor.greenHouseEmissionType.length > 0
                  ? factor.greenHouseEmissionType
                  : metadataGreenHouseEmissionType;
              return ghgs.includes(g.gasKey as EmissionSourceGreenHouseGases);
            })
            .map((gas) => getGhgFamily({ ghg: gas.gasKey, isRefrigerant: gas.isRefrigerant, isPFCS: gas.isPFCS })),
      )
      .flat() || [];

  return uniq(ghgI18nList)
    .map((g) => (g === EmissionSourceGreenHouseGases.NA ? t('ghg.NA') : g ? t(`ghg.${g}`) : ''))
    .join(', ');
};

export const mapApprovalDetailsToReviewEmissionsType = (data?: ApprovalLog) => {
  const { id, status, submittedBy, reviewedAt, isBatch, activityData } = data || {};

  return {
    status,
    submittedBy,
    reviewedAt,
    approval: { status, id, isBatch, reviewedAt, submittedBy, activityData },
    ...activityData,
  } as ReviewEmissionsType;
};

export const isAerosolSprayWithHfcs = (
  ghgTypes: EmissionSourceGreenHouseGases[],
  fugitiveCategory?: string | null,
): boolean => {
  const fugitiveCategoryType = getFugitiveCategoryType(fugitiveCategory);
  if (fugitiveCategoryType !== FugitiveCategoryType.AEROSOL_SPRAY || !isArray(ghgTypes)) {
    return false;
  }
  return ghgTypes.some((ghg) => ghg === EmissionSourceGreenHouseGases.HFCS);
};

export const isGhgOnlyHasNA = (ghgTypes: Array<EmissionSourceGreenHouseGases | undefined>): boolean => {
  if (!isArray(ghgTypes)) {
    return false;
  }

  return ghgTypes.length === 1 && ghgTypes[0] === EmissionSourceGreenHouseGases.NA;
};

export const isNeedToShowCo2e = (subCategory?: EmissionSourceSubCategory, isCO2Equivalent?: boolean): boolean => {
  if (isCO2Equivalent !== undefined) {
    return isCO2Equivalent;
  }
  if (subCategory) {
    return CreateEmissionConstants.ONLY_SHOW_CO2E_GHG_SUB_CATEGORIES.includes(subCategory);
  }
  return false;
};

export const transferActivityDataResToForm = ({
  emission,
  activityData,
}: {
  emission: EmissionType;
  activityData: ReviewEmissionsType;
}) => {
  let result = cloneDeep(activityData);

  result.documentDate = activityData.documentDate ? new Date(activityData.documentDate) : null;
  result.documentEndDate = activityData.documentEndDate ? new Date(activityData.documentEndDate) : null;

  const wastewaterType = getWastewaterType(emission.metadata.fugitiveType);
  switch (wastewaterType) {
    case WastewaterType.COMMERCIAL:
      result = transferActivityDataResToFormCat1_4WastewaterCommercial(result);
      break;
    case WastewaterType.INDUSTRIAL:
      result = transferActivityDataResToFormCat1_4WastewaterIndustrial(result);
      break;
    case WastewaterType.OTHER:
      result = transferActivityDataResToFormCat1_4WastewaterOther(result);
    default:
      break;
  }

  return result;
};

export const transferEmissionsDisplay = (
  t: NextTFunction,
  arg: {
    value: number | undefined | null;
    unit?: ActivityUnit;
    isOnlyValue?: boolean;
  },
) => {
  const { value, unit = '', isOnlyValue = false } = arg;

  if (!value) {
    return '-';
  }

  const emissions = formatFullDecimal(value);

  if (isOnlyValue) {
    return emissions;
  }

  return `${emissions} ${t(`unit.${unit}`)} ${t('ghg.CO2E')}`;
};

export const amountConversion = (amount?: number | null): string => {
  let finalAmount = '';
  try {
    finalAmount = new Big(amount as unknown as number).toFixed();
  } catch (error) {}

  return formatNum(finalAmount, { decimalPlaces: 10, padZero: false });
};

export const unitConversion = (t: NextTFunction, unit?: ActivityUnit | string | null): string => {
  return safeTranslate(t, `unit.${unit}`, '');
};

const transferActivityDataResToFormCat1_4WastewaterCommercial = (activityData: ReviewEmissionsType) => {
  const result = cloneDeep(activityData);

  result.metadata.wastewaterActivity.emitDays = activityData.metadata.wastewaterActivity.dischargeDays;
  result.metadata.wastewaterActivity.sewageTreatmentPopulation =
    result.metadata.wastewaterActivity.wastewaterTreatmentPlantRegisteredPopulation;
  result.metadata.wastewaterActivity.nitrogenRemovalAmount =
    result.metadata.wastewaterActivity.nitrogenRemovalDueToSludge;
  result.metadata.usageRatio = toNumber(result.metadata.wastewaterActivity.activityDataAllocationRatio);
  result.metadata.wastewaterActivity.sludgeRemovalAmount = result.metadata.wastewaterActivity.sludgeRemovalVolume;
  result.metadata.wastewaterActivity.convertSludgeRemovalAmount =
    result.metadata.wastewaterActivity.convertSludgeRemovalVolume;
  result.metadata.wastewaterActivity.recycleCh4Amount = result.metadata.wastewaterActivity.methaneRecoveryVolume;
  result.metadata.wastewaterActivity.recycleCh4Unit = result.metadata.wastewaterActivity.methaneRecoveryUnit;
  result.metadata.wastewaterActivity.convertRecycleCh4Amount =
    result.metadata.wastewaterActivity.convertMethaneRecoveryVolume;
  result.metadata.wastewaterActivity.convertRecycleCh4Unit =
    result.metadata.wastewaterActivity.convertMethaneRecoveryUnit;

  return result;
};

const transferActivityDataResToFormCat1_4WastewaterIndustrial = (activityData: ReviewEmissionsType) => {
  const result = cloneDeep(activityData);

  activityData.metadata.usageRatio = toNumber(result.metadata.wastewaterActivity.activityDataAllocationRatio);
  result.metadata.wastewaterActivity.sludgeRemovalAmount = result.metadata.wastewaterActivity.sludgeRemovalVolume;
  result.metadata.wastewaterActivity.convertSludgeRemovalAmount =
    result.metadata.wastewaterActivity.convertSludgeRemovalVolume;
  result.metadata.wastewaterActivity.recycleCh4Amount = result.metadata.wastewaterActivity.methaneRecoveryVolume;
  result.metadata.wastewaterActivity.recycleCh4Unit = result.metadata.wastewaterActivity.methaneRecoveryUnit;
  result.metadata.wastewaterActivity.convertRecycleCh4Amount =
    result.metadata.wastewaterActivity.convertMethaneRecoveryVolume;
  result.metadata.wastewaterActivity.convertRecycleCh4Unit =
    result.metadata.wastewaterActivity.convertMethaneRecoveryUnit;
  result.metadata.wastewaterActivity.totalNitrogenConcentrationUnitInTreatedWaterNumerator =
    result.metadata.wastewaterActivity.totalNitrogenConcentrationUnitNumeratorInTreatedWater;
  result.metadata.wastewaterActivity.totalNitrogenConcentrationUnitInTreatedWaterDenominator =
    result.metadata.wastewaterActivity.totalNitrogenConcentrationUnitDenominatorInTreatedWater;
  result.metadata.wastewaterActivity.convertTotalNitrogenConcentrationUnitInTreatedWaterNumerator =
    result.metadata.wastewaterActivity.convertTotalNitrogenConcentrationUnitNumeratorInTreatedWater;
  result.metadata.wastewaterActivity.convertTotalNitrogenConcentrationUnitInTreatedWaterDenominator =
    result.metadata.wastewaterActivity.convertTotalNitrogenConcentrationUnitDenominatorInTreatedWater;

  return result;
};

const transferActivityDataResToFormCat1_4WastewaterOther = (activityData: ReviewEmissionsType) => {
  const result = cloneDeep(activityData);

  result.metadata.wastewaterActivity.usageAmount = result.metadata.convertedUsageAmount;
  result.metadata.wastewaterActivity.usageUnit = result.metadata.convertedUnit;

  return result;
};

/**
 * Get Activity Entry/Approval/Collection layout title
 * Note that Cat 3.3 Employee Id title will be defined only in DataCollectionDetailsCat3_3
 * it will have no title for Cat 3.3 for Entry and Approval
 * @param data
 * @param emission
 * @param hasBulkData
 * @returns string
 */
export const getActivityDetailsTitle = (
  data: ReviewEmissionsType | undefined,
  subCategory: EmissionSourceSubCategory | undefined,
  hasBulkData: boolean = false,
): string => {
  if (!data || !subCategory) {
    return '';
  }

  const isSerialNumber = [
    EmissionSourceSubCategory.CAT_3_1,
    EmissionSourceSubCategory.CAT_3_2,
    EmissionSourceSubCategory.CAT_4_1A,
    EmissionSourceSubCategory.CAT_5_1,
    EmissionSourceSubCategory.CAT_5_3,
    EmissionSourceSubCategory.CAT_5_4,
  ].includes(subCategory);
  const isActivityDataName = [
    EmissionSourceSubCategory.CAT_4_2,
    EmissionSourceSubCategory.CAT_4_4,
    EmissionSourceSubCategory.CAT_5_2,
    EmissionSourceSubCategory.CAT_5_4,
    EmissionSourceSubCategory.CAT_5_5,
    EmissionSourceSubCategory.CAT_5_6,
  ].includes(subCategory);

  const metadata = data.metadata;
  if (!hasBulkData && isActivityDataName) {
    return metadata.name ?? '';
  } else if (isSerialNumber) {
    const serialNumber = hasBulkData ? metadata.listSerialNo : metadata.sourceTypeSerialNo;
    return serialNumber ?? '';
  }
  return '';
};
